import { useMemo, useState } from 'react';

import type { CapturedRequest } from '../lib/api.js';
import { decodeBody, formatBytes } from '../lib/body.js';
import { CopyButton } from './CopyButton.js';
import { HexViewer } from './HexViewer.js';
import { JsonTree } from './JsonTree.js';

export interface BodyViewerProps {
  request: CapturedRequest;
}

/** Renders a captured body the way its `content-type` says it should be read. */
export function BodyViewer({ request }: BodyViewerProps) {
  const decoded = useMemo(() => decodeBody(request), [request]);
  const [raw, setRaw] = useState(false);

  if (decoded.kind === 'empty') {
    return <p className="text-sm text-slate-500">Sin cuerpo.</p>;
  }

  const canToggleRaw = decoded.kind === 'json' || decoded.kind === 'form';

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2 text-xs text-slate-400">
        <span className="rounded bg-slate-800 px-2 py-0.5 font-mono">
          {request.contentType ?? 'sin content-type'}
        </span>
        <span>{formatBytes(request.bodySize)}</span>

        {request.bodyTruncated && (
          // Loud on purpose: a truncated body looks like a complete one, and
          // reading it as complete is how people chase bugs that are not there.
          <span className="rounded bg-amber-500/15 px-2 py-0.5 font-medium text-amber-300 ring-1 ring-amber-500/30">
            Truncado a 256 KB — se recibieron {formatBytes(request.bodySize)}
          </span>
        )}

        {decoded.malformed && (
          <span className="rounded bg-rose-500/15 px-2 py-0.5 font-medium text-rose-300 ring-1 ring-rose-500/30">
            El content-type dice JSON pero el cuerpo no parsea
          </span>
        )}

        <span className="ml-auto flex items-center gap-2">
          {canToggleRaw && (
            <button
              type="button"
              onClick={() => setRaw((current) => !current)}
              className="rounded-md border border-slate-700 bg-slate-800 px-2.5 py-1 font-medium text-slate-200 hover:bg-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400"
            >
              {raw ? 'Ver formateado' : 'Ver en crudo'}
            </button>
          )}
          <CopyButton
            value={decoded.kind === 'binary' ? (request.body ?? '') : decoded.text}
            label={decoded.kind === 'binary' ? 'Copiar base64' : 'Copiar cuerpo'}
          />
        </span>
      </div>

      {decoded.kind === 'binary' && decoded.bytes ? (
        <HexViewer bytes={decoded.bytes} />
      ) : decoded.kind === 'json' && !raw ? (
        <div className="rounded-md bg-slate-950/60 p-3">
          <JsonTree value={decoded.json} />
        </div>
      ) : decoded.kind === 'form' && !raw && decoded.pairs ? (
        <FormTable pairs={decoded.pairs} />
      ) : (
        <pre className="overflow-x-auto whitespace-pre-wrap break-all rounded-md bg-slate-950/60 p-3 font-mono text-xs leading-relaxed text-slate-200">
          {decoded.text}
        </pre>
      )}
    </div>
  );
}

/** Ordered pairs, not an object: a form may repeat a key and both matter. */
function FormTable({ pairs }: { pairs: [string, string][] }) {
  if (pairs.length === 0) {
    return <p className="text-sm text-slate-500">El cuerpo está vacío.</p>;
  }

  return (
    <div className="overflow-x-auto rounded-md bg-slate-950/60">
      <table className="w-full text-left font-mono text-xs">
        <tbody>
          {pairs.map(([key, value], index) => (
            <tr key={`${key}-${index}`} className="border-b border-slate-800/60 last:border-0">
              <td className="w-1/3 px-3 py-1.5 align-top text-sky-300">{key}</td>
              <td className="whitespace-pre-wrap break-all px-3 py-1.5 text-slate-200">{value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
